import React, {useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
    IonAlert,
    IonButtons,
    IonHeader,
    IonPage,
    isPlatform,
    useIonViewWillEnter,
    useIonViewWillLeave
} from '@ionic/react';
import {
    StyledEmptyContainer,
    StyledIonContentCameraPreview,
    StyledIonTitle,
    StyledIonToolbar
} from '../../components/content/content.style';
import {
    StyledButton,
    StyledHeaderButton,
    StyledHeaderButtonImage,
    StyledSocialButton
} from '../../components/button/button.style';

import {
    ArrowLeftImage,
    WarningImage,
    CameraPreviewImage
} from "../../utils/styles/icons";

import {useTranslation} from 'react-i18next';
import {StyledContent} from '../takePhotoPage/takePhotoPage.style';
import {getWorkerConfiguration} from '../../services/qrCodePage.service';
import CircleImage, {ImageTypes} from "../../components/circleImage/circleImage.component";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    makeWebViewTransparent,
    restoreWebView,
    BARCODE_QR_FORMATS,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";

const QRCodeWorkerPage: React.FC = () => {

    const {t} = useTranslation();
    const history = useHistory();

    const handleBack = () => {
        history.goBack();
    };

    const configuration = getWorkerConfiguration();

    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [showError, setShowError] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);

    useEffect(() => {
        updateHeight();
    })

    const startScan = async () => {
        makeWebViewTransparent();

        const handle = await startContinuousScan(BARCODE_QR_FORMATS, async (rawValue) => {
            if (rawValue) {
                if (configuration?.handleScan) {
                    let error = await configuration?.handleScan(rawValue);
                    if (error === false) {
                        stopScan()
                        setShowError(true);
                    }
                }
            }
        });

        scanHandleRef.current = handle;
    };

    useIonViewWillEnter(() => {
        if (isPlatform('ios') || isPlatform('android')) {
            start();
        }
    });

    const start = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
        }
        startScan();
        setShowError(false);
    }

    const stopScan = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();
    };

    useIonViewWillLeave(() => {
        if (isPlatform('ios') || isPlatform('android')) {
            stopScan();
        }
    });

    if (!configuration) {
        history.goBack();
    }

    const updateHeight = () => {
        if (header.current?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    const header = useRef<HTMLIonHeaderElement>(null);

    return (
        <IonPage>
            <StyledIonContentCameraPreview>
                <img className="background" src={CameraPreviewImage}/>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <IonButtons slot="start">
                            <StyledHeaderButton className="back-btn" onClick={() => handleBack()}>
                                <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                        <StyledIonTitle>{t(configuration?.title ?? '')}</StyledIonTitle>
                    </StyledIonToolbar>
                    <div className="sub-title-light">{t(configuration?.subtitle ?? '')}</div>
                </IonHeader>
                <StyledContent>
                    <div className="target">
                        {
                            showError &&
                            <StyledEmptyContainer className="preview">
                                <CircleImage onClick={async (e) => {
                                    start();
                                }} image={ImageTypes.QR} color="red" />
                            </StyledEmptyContainer>
                        }
                    </div>
                    <div className={'action-buttons empty'}>
                        {showError && <StyledSocialButton className="red"><img src={WarningImage} />{t(configuration?.errorText || '')}</StyledSocialButton>}
                        {configuration?.manualButtonText &&
                            <StyledButton onClick={() => configuration?.handleManualButton && configuration?.handleManualButton()}>{t(configuration?.manualButtonText)}</StyledButton>}
                    </div>
                </StyledContent>
            </StyledIonContentCameraPreview>
            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </IonPage>
    );
};

export default QRCodeWorkerPage;
